/**
 * @description: 通用的AJAX请求封装，
 * @return 使用方式 import Request, { $request, $get, $post, $put, $delete } from '@/utils/request';
 */
import { ElMessage, ElMessageBox } from 'element-plus';
import Qs from 'qs';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Canceler,
  AxiosError,
} from 'axios';
import { LocalStorageService, SessionStorageService } from '@/utils/storage';
import { getLogoutUrl } from '@/utils/index';

const {
  DEV: isDev,
  VITE_APP_BASE_API: baseApi,
  VITE_APP_GAIA_API_KEY: apiKey,
} = import.meta.env;
const sourceAjaxList: Canceler[] = [];
let is402DialogShowing = false; // 全局弹窗标记，确保只弹出一个

const clearStorage = () => {
  LocalStorageService.clear();
  SessionStorageService.clear();
};

/**
 * @description: 需要主动调用new Request(), 可传递更多配置参数进去
 * @return {*}
 */
export class Request {
  instance: AxiosInstance;

  baseConfig: AxiosRequestConfig = {
    headers: {
      'X-Gaia-Api-Key': apiKey,
    },
    baseURL: isDev ? '' : baseApi,
    timeout: 30000,
    withCredentials: true,
  };

  constructor(axiosConfig?: AxiosRequestConfig) {
    this.instance = axios.create(Object.assign(this.baseConfig, axiosConfig));

    this.instance.interceptors.request.use(
      (requestConfig: AxiosRequestConfig) => {
        // 设置 cancel token  用于取消请求 (当一个接口出现401后，取消后续多有发起的请求，避免出现好几个错误提示)
        requestConfig.cancelToken = new axios.CancelToken(
          (cancel: Canceler): void => {
            sourceAjaxList.push(cancel);
          }
        );

        // 这里一个token，你需要在这里取到你设置好的token的值 示例逻辑代码
        const token = LocalStorageService.get('_user_info')?.authorization || '';

        const currentTenant = LocalStorageService.get('_current_tenant') || null;

        if (token) {
          // 这里将token设置到headers中，header的key是Authorization，这个key值根据你的需要进行修改即可
          requestConfig.headers = {
            Authorization: token,
            ...requestConfig.headers,
          };
        }

        // 添加租户ID到请求头
        if (currentTenant?.mid) {
          requestConfig.headers = {
            ...requestConfig.headers,
            tenantId: currentTenant.mid,
          };
        }

        requestConfig.paramsSerializer = (params) => Qs.stringify(params, { arrayFormat: 'repeat' });

        return requestConfig;
      },
      (error: any) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        const requestUrl = response.config.url || '';
        const { headers, data: result } = response;

        // 后端直接返回文件流
        if (
          !headers['content-type']?.includes('application/json')
          || result instanceof Blob
        ) {
          return response;
        }
        // 返回数据的code 不为0、200即为数据出错
        if (+result.code === 0 || result.code === '200') {
          return result;
        }
        if (+result.code === 401) {
          clearStorage();
          location.href = getLogoutUrl(result.loginUrl || '');
          throw new Error(`${result.loginUrl} 需要重新登录`);
        }
        if (+result.code === 403) {
          ElMessage({
            message: `${requestUrl} 暂无访问权限`,
            type: 'error',
            grouping: true,
          });
          throw new Error(`${requestUrl} 暂无访问权限`);
        }
        if (+result.code === 402) {
          // 确保全局只弹出一个弹窗
          if (!is402DialogShowing) {
            is402DialogShowing = true;
            // 在清除租户信息之前，先获取租户对应的域名
            const currentTenant = LocalStorageService.get('_current_tenant') as any;
            let tenantDomain = '';
            try {
              let expandInfo = currentTenant?.expandInfo;
              if (typeof expandInfo === 'string') {
                expandInfo = JSON.parse(expandInfo);
              }
              tenantDomain = expandInfo?.tenantDomain || '';
            } catch (e) {
              console.error('解析租户域名失败:', e);
            }
            let redirectUrl = window.location.origin;
            if (tenantDomain) {
              redirectUrl = tenantDomain.startsWith('http') ? tenantDomain : `https://${tenantDomain}`;
            }
            ElMessageBox.confirm('当前页面已失效，请刷新页面', '页面已失效', {
              confirmButtonText: '刷新页面',
              cancelButtonText: '取消',
              type: 'warning',
              closeOnClickModal: false,
              closeOnPressEscape: false,
            })
              .then(() => {
                // 点击确定，清空租户信息并跳转到租户对应的域名
                LocalStorageService.remove('_current_tenant');
                window.location.href = redirectUrl;
              })
              .catch(() => {
                // 点击取消或关闭，也清空租户信息并跳转到租户对应的域名
                LocalStorageService.remove('_current_tenant');
                window.location.href = redirectUrl;
              })
              .finally(() => {
                is402DialogShowing = false;
              });
          }
          throw new Error('页面已失效');
        }

        ElMessage({
          message: result.message || result.msg || 'Error',
          type: 'error',
          grouping: true,
          dangerouslyUseHTMLString: true,
        });
        throw new Error(result.message || result.msg || 'Error'); // 预期结果失败
      },
      (err: AxiosError) => {
        if (err.toString().includes('timeout')) {
          ElMessage({
            message: '请求超时，请稍后再试',
            type: 'error',
            grouping: true,
          });
        }
        if (err && err.response) {
          const { status, data, config }: any = err.response;
          const requestUrl = err.config.url || '';
          switch (status) {
            case 400:
              err.message = '请求错误';
              break;

            case 401:
              // 用户登录信息认证失败，退出到登录页之前清空缓存内容
              clearStorage();
              location.href = getLogoutUrl(data.loginUrl || '');
              // 出现未登录或登录失效取消后面的请求
              if (sourceAjaxList.length > 0) {
                sourceAjaxList.forEach((ajaxCancel, index) => {
                  ajaxCancel(); // 取消请求
                  delete sourceAjaxList[index];
                });
              }
              break;

            case 403:
              err.message = `${requestUrl} 暂无访问权限`;
              break;

            case 404:
              err.message = `请求地址出错: ${config.url}`;
              break;

            case 408:
              err.message = '请求超时';
              break;

            case 500:
            case 501:
            case 502:
            case 503:
            case 504:
              err.message = '服务器开个小差，请稍后再试';
              break;

            case 505:
              err.message = 'HTTP版本不受支持';
              break;

            default:
              err.message = '请求失败，请稍后再试';
          }
          ElMessage({
            message: err.message,
            type: 'error',
            grouping: true,
          });
        }
        return Promise.reject(err);
      }
    );
  }

  public request<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.instance.request(config);
  }

  public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.get(url, config);
  }

  public post<T = any>(
    url: string,
    data?: TypeObject,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.instance.post(url, data, config);
  }

  public put<T = any>(
    url: string,
    data?: TypeObject,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.instance.put(url, data, config);
  }

  public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.delete(url, config);
  }
}

/**
 * @description: 封装默认配置的axios的request、get、post、put、delete
 * @return {*}
 */
const server = new Request();
const $request = <T = any>(config: AxiosRequestConfig): Promise<T> => server.request(config);

const $get = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => server.get(url, config);

const $post = <T = any>(
  url: string,
  data?: TypeObject,
  config?: AxiosRequestConfig
): Promise<T> => server.post<T>(url, data, config);

const $put = <T = any>(
  url: string,
  data?: TypeObject,
  config?: AxiosRequestConfig
): Promise<T> => server.put<T>(url, data, config);

const $delete = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => server.delete<T>(url, config);

export {
  $request, $get, $post, $put, $delete,
};
export default server;
